import { t } from 'i18next'

import type { TPropostaSchemaInput } from '@/features/proposta/brasil/domain/schemas'
import type { TCompradoresCadastrados } from '@/features/proposta/brasil/types/proposta.type'

export type PersonType = 'PessoaFisica' | 'PessoaJuridica' | 'MaePai' | 'Procurador' | 'Representante' | 'Conjuge'
type OrigemVinculo = 'Comprador' | 'MaePai' | 'Representante'
type ResumoElegivel = {
  id: string
  tipo: 'PessoaFisica' | 'PessoaJuridica'
  nome: string
  documento: string
  origem: OrigemVinculo
  jaEstaVinculado?: boolean
}

const opcoesPadrao: Array<{ id: PersonType; label: string }> = [
  { id: 'PessoaFisica', label: t('proposta:cadastro_compradores.popover.opcoes.pessoa_fisica') },
  { id: 'PessoaJuridica', label: t('proposta:cadastro_compradores.popover.opcoes.pessoa_juridica') },
]

export const obterOpcoesCadastroComprador = (
  compradores: TCompradoresCadastrados,
): Array<{ id: PersonType; label: string }> => {
  const opcoesExtras: Array<{ id: PersonType; label: string }> = []

  const menor18Anos = compradores.find((comprador) => comprador.maiorDe18Anos === false)
  const deveExibirOpcaoRepresentanteLegal = compradores.some((comprador) => comprador.tipo === 'PessoaJuridica')
  const deveExibirOpcaoProcurador = compradores.length

  if (menor18Anos) {
    opcoesExtras.push({
      id: 'MaePai',
      label: t('proposta:cadastro_compradores.popover.opcoes.mae_pai'),
    })
  }

  if (deveExibirOpcaoProcurador) {
    opcoesExtras.push({
      id: 'Procurador',
      label: t('proposta:cadastro_compradores.popover.opcoes.procurador'),
    })
  }

  if (deveExibirOpcaoRepresentanteLegal) {
    opcoesExtras.push({
      id: 'Representante',
      label: t('proposta:cadastro_compradores.popover.opcoes.representante'),
    })
  }

  return [...opcoesPadrao, ...opcoesExtras]
}

export function obterElegiveisParaProcurador(
  compradoresCadastrados?: TCompradoresCadastrados,
  procuradores?: TPropostaSchemaInput['cadastroCompradores']['procuradores'],
  procuradorAtualId?: string,
): ResumoElegivel[] {
  const vinculos: TCompradoresCadastrados[0]['vinculos'] = []
  if (!compradoresCadastrados) return []
  const compradores: ResumoElegivel[] = compradoresCadastrados
    ?.map((comprador) => {
      vinculos.push(...comprador.vinculos)
      if (comprador.maiorDe18Anos === false) return null
      return {
        id: comprador.id,
        tipo: comprador.tipo,
        nome: comprador.nome,
        documento: comprador.documento,
        origem: 'Comprador' as OrigemVinculo,
        jaEstaVinculado: procuradores?.length
          ? procuradores.some((p) => p.clientesIds.includes(comprador.id) && p.procuradorId === procuradorAtualId)
          : false,
      }
    })
    .filter((c) => c !== null)

  const clientesComoVinculo: ResumoElegivel[] = vinculos
    .filter((vinculo) => vinculo.tipo !== 'Conjuge')
    .map((vinculo) => {
      return {
        documento: vinculo.documento,
        id: vinculo.id,
        tipo: 'PessoaFisica' as const,
        nome: vinculo.nome,
        origem: vinculo.tipo as OrigemVinculo,
        jaEstaVinculado: procuradores?.length
          ? procuradores.some((p) => p.clientesIds.includes(vinculo.id) && p.procuradorId === procuradorAtualId)
          : false,
      }
    })

  const elegiveis = [...compradores, ...clientesComoVinculo]

    ?.filter((item, index, listaElegiveis) => listaElegiveis.findIndex((elegivel) => elegivel.id === item.id) === index)
    .filter((elegivel) => elegivel.id !== procuradorAtualId)

  return elegiveis.toSorted((a, b) => a.nome.localeCompare(b.nome))
}
